// Live cross-tab mirroring for in-progress video uploads.
//
// videoUploads is driven by a real TUS Upload + poll interval that only
// exists in the JS heap of the tab that started it — it can never be
// reconstructed from localStorage in another tab (see localStorage.tsx,
// which now deliberately resets it instead of persisting it). Instead, the
// owning tab broadcasts every state change over a BroadcastChannel and
// every other open tab (same origin) just mirrors it into its own Redux
// store, so all tabs show the exact same live progress instead of a
// stale/frozen copy.
import type { Store } from "@reduxjs/toolkit";
import { addVideoUpload, updateVideoUpload, removeVideoUpload } from "@/core/redux/commonSlice";
import type { VideoUploadEntry } from "@/core/redux/commonSlice";

const CHANNEL_NAME = "video-upload-sync";

const channel: BroadcastChannel | null =
  typeof BroadcastChannel !== "undefined" ? new BroadcastChannel(CHANNEL_NAME) : null;

type SyncMessage =
  | { type: "add"; entry: VideoUploadEntry }
  | { type: "update"; updates: Partial<VideoUploadEntry> & { uploadId: string } }
  | { type: "remove"; uploadId: string }
  | { type: "hydrate-request" }
  | { type: "hydrate-response"; entries: VideoUploadEntry[] };

export function initVideoUploadSync(store: Store): void {
  if (!channel) return;

  channel.onmessage = (event: MessageEvent<SyncMessage>) => {
    const msg = event.data;
    const current: VideoUploadEntry[] = (store.getState() as any)?.common?.videoUploads ?? [];

    switch (msg.type) {
      case "add":
        if (!current.some((u) => u.uploadId === msg.entry.uploadId)) {
          store.dispatch(addVideoUpload(msg.entry));
        }
        break;
      case "update":
        store.dispatch(updateVideoUpload(msg.updates));
        break;
      case "remove":
        store.dispatch(removeVideoUpload(msg.uploadId));
        break;
      case "hydrate-request":
        if (current.length > 0) {
          channel.postMessage({ type: "hydrate-response", entries: current } as SyncMessage);
        }
        break;
      case "hydrate-response":
        msg.entries.forEach((entry) => {
          if (!current.some((u) => u.uploadId === entry.uploadId)) {
            store.dispatch(addVideoUpload(entry));
          }
        });
        break;
    }
  };

  // Ask any tab that's already mid-upload to share its current entries, so a
  // freshly opened/reloaded tab reflects in-flight uploads immediately
  // instead of waiting for the next progress tick from the owning tab.
  channel.postMessage({ type: "hydrate-request" } as SyncMessage);
}

export const broadcastVideoUploadAdd = (entry: VideoUploadEntry): void => {
  channel?.postMessage({ type: "add", entry } as SyncMessage);
};

export const broadcastVideoUploadUpdate = (
  updates: Partial<VideoUploadEntry> & { uploadId: string },
): void => {
  channel?.postMessage({ type: "update", updates } as SyncMessage);
};

export const broadcastVideoUploadRemove = (uploadId: string): void => {
  channel?.postMessage({ type: "remove", uploadId } as SyncMessage);
};
